import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { stashPendingPlan, readPendingPlan, clearPendingPlan } from "@/lib/paddle";
import { toast } from "sonner";
import {
    isPasskeyFeatureEnabled, isConditionalMediationAvailable,
    signInWithPasskey, passkeyButtonLabel,
} from "@/lib/webauthn";
import {
    Eye, EyeOff, Lock, Mail, ShieldOff, Fingerprint, ArrowRight, ShieldCheck,
} from "lucide-react";
import VaultStoryStage from "@/components/login/VaultStoryStage";
import "@/styles/login-stage.css";

/**
 * VAULT · Login v4 (Feb 2026) — Midnight Luxe rotating stage.
 *
 * • Left canvas is a 5-chapter narrative (Global → Cards → Wealth → Life
 *   → Together) that loops every ~30 seconds.  See VaultStoryStage.jsx.
 * • Right panel is a calm, fixed sign-in form.  Auth options shown match
 *   what the backend actually implements: password + WebAuthn passkey.
 * • Mobile: form first, storytelling below — never make the visitor
 *   scroll past marketing to authenticate.
 * • Nothing on this page reads or renders authenticated data.
 */
function track(event, meta) {
    try {
        if (typeof window !== "undefined" && typeof window.vlxAnalytics === "function") {
            window.vlxAnalytics(event, meta || {});
        }
    } catch { /* silent */ }
}

export default function Login() {
    const { user, error, login, verify2FA } = useAuth();
    const [params] = useSearchParams();
    const isDemo = params.get("demo") === "1";

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(!!isDemo);
    const [twoFA, setTwoFA] = useState(null);
    const [otp, setOtp] = useState("");
    const [passkeyReady, setPasskeyReady] = useState(false);
    const [passkeyBusy, setPasskeyBusy] = useState(false);

    /* first paint → analytics ping */
    useEffect(() => { track("login_page_view"); }, []);

    /* passkey capability + conditional-UI autofill */
    useEffect(() => {
        let cancelled = false;
        const abort = new AbortController();
        (async () => {
            const flag = await isPasskeyFeatureEnabled();
            const supported = typeof window !== "undefined"
                && !!window.PublicKeyCredential
                && !!navigator.credentials?.get;
            if (cancelled) return;
            setPasskeyReady(flag && supported);
            if (!flag || !supported) return;
            const conditional = await isConditionalMediationAvailable();
            if (!conditional || cancelled) return;
            try {
                const u = await signInWithPasskey({ mediation: "conditional", signal: abort.signal });
                if (u) window.location.replace("/dashboard");
            } catch { /* user chose to type */ }
        })();
        return () => { cancelled = true; abort.abort(); };
    }, []);

    /* demo tenant auto-login (?demo=1) */
    useEffect(() => {
        if (!isDemo || user) return;
        (async () => {
            try {
                await api.post("/demo-workspace/login");
                window.location.replace("/dashboard");
            } catch { setBusy(false); }
        })();
    }, [isDemo, user]);

    /* preserve `?plan=` → resume checkout after sign-in */
    useEffect(() => {
        const plan = params.get("plan");
        if (plan) stashPendingPlan({
            plan, billing: params.get("billing") || "monthly",
            currency: params.get("ccy"), ref: params.get("ref"), promo: params.get("promo"),
        });
    }, [params]);

    if (user && user !== false) {
        const pending = readPendingPlan();
        if (pending?.plan) {
            clearPendingPlan();
            return <Navigate to={`/checkout/confirm?plan=${pending.plan}&billing=${pending.billing || "monthly"}`} replace />;
        }
        return <Navigate to="/dashboard" replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        track("login_signin_click");
        setBusy(true);
        const res = await login(username, password);
        setBusy(false);
        if (res && typeof res === "object" && res.requires2FA) setTwoFA({ token: res.challengeToken });
    };

    const onVerify = async (e) => {
        e.preventDefault();
        setBusy(true);
        await verify2FA(twoFA.token, otp);
        setBusy(false);
    };

    const onPasskeySignIn = async () => {
        track("login_passkey_click");
        setPasskeyBusy(true);
        try {
            await signInWithPasskey({ email: username || undefined });
            window.location.replace("/dashboard");
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Sign in cancelled.";
            if (!/cancel/i.test(msg)) toast.error(msg);
        } finally { setPasskeyBusy(false); }
    };

    return (
        <div className="vlx" data-testid="login-page">
            <div className="vlx-body">
                {/* ═══════ LEFT · rotating VAULT story ═══════ */}
                <VaultStoryStage />

                {/* ═══════ RIGHT · calm sign-in ═══════ */}
                <section className="vlx-panel" aria-label="Sign in to VAULT">
                    {twoFA ? (
                        <form onSubmit={onVerify} className="vlx-card-signin" data-testid="login-2fa-form">
                            <div className="vlx-brand-line">
                                <ShieldCheck size={18} /> VAULT <small>by PD</small>
                            </div>
                            <h1 className="vlx-welcome" style={{ fontSize: 28 }}>Two-factor</h1>
                            <p className="vlx-sub">Enter the 6-digit code from your authenticator.</p>
                            <input
                                type="text"
                                inputMode="numeric"
                                autoFocus
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\s/g, ""))}
                                placeholder="123 456"
                                className="vlx-otp"
                                data-testid="login-2fa-input"
                            />
                            {error && (
                                <div className="vlx-error" data-testid="login-2fa-error">
                                    <ShieldOff size={13} /> <span>{error}</span>
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={busy || otp.length < 6}
                                className="vlx-cta"
                                data-testid="login-2fa-submit"
                            >
                                {busy ? "Verifying…" : "Verify & sign in"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setTwoFA(null); setOtp(""); }}
                                className="vlx-back"
                                data-testid="login-2fa-back"
                            >
                                ← Back to sign in
                            </button>
                            <div className="vlx-reassure">
                                <Lock size={12} /> VAULT never asks for your CVV, PIN or OTP outside 2FA.
                            </div>
                        </form>
                    ) : (
                        <form onSubmit={onSubmit} className="vlx-card-signin" data-testid="login-form">
                            <div className="vlx-brand-line" data-testid="login-brand">
                                <ShieldCheck size={18} /> VAULT <small>by PD</small>
                            </div>

                            <h1 className="vlx-welcome">Welcome back.</h1>
                            <p className="vlx-sub">Continue to your VAULT.</p>

                            <div className="vlx-form">
                                <div className="vlx-field">
                                    <label htmlFor="vlx-email" className="vlx-field__label">Email</label>
                                    <div className="vlx-field__wrap">
                                        <Mail size={16} className="vlx-field__icon" strokeWidth={1.9} />
                                        <input
                                            id="vlx-email"
                                            type="text"
                                            autoComplete="username webauthn"
                                            data-testid="login-username-input"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            placeholder="you@vault.com"
                                            required
                                            className="vlx-field__input"
                                        />
                                    </div>
                                </div>

                                <div className="vlx-field">
                                    <label htmlFor="vlx-password" className="vlx-field__label">Password</label>
                                    <div className="vlx-field__wrap">
                                        <Lock size={16} className="vlx-field__icon" strokeWidth={1.9} />
                                        <input
                                            id="vlx-password"
                                            type={showPw ? "text" : "password"}
                                            autoComplete="current-password"
                                            data-testid="login-password-input"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Your password"
                                            required
                                            className="vlx-field__input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPw((v) => !v)}
                                            className="vlx-field__eye"
                                            data-testid="login-toggle-password"
                                            aria-label={showPw ? "Hide password" : "Show password"}
                                            tabIndex={-1}
                                        >
                                            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="vlx-row">
                                    <span aria-hidden />
                                    <Link
                                        to="/forgot-password"
                                        className="vlx-forgot"
                                        data-testid="login-forgot-link"
                                        onClick={() => track("login_forgot_click")}
                                    >
                                        Forgot password?
                                    </Link>
                                </div>

                                {error && (
                                    <div className="vlx-error" data-testid="login-error">
                                        <ShieldOff size={13} /> <span>{error}</span>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={busy}
                                    className="vlx-cta"
                                    data-testid="login-submit-btn"
                                >
                                    {busy ? "Signing in…" : (<>Sign in <ArrowRight size={16} /></>)}
                                </button>

                                {passkeyReady && (
                                    <>
                                        <div className="vlx-divider" data-testid="login-passkey-divider">or</div>
                                        <button
                                            type="button"
                                            onClick={onPasskeySignIn}
                                            disabled={passkeyBusy}
                                            className="vlx-cta-ghost"
                                            data-testid="login-passkey-btn"
                                        >
                                            <Fingerprint size={16} />
                                            {passkeyBusy ? "Waiting for authenticator…" : passkeyButtonLabel()}
                                        </button>
                                    </>
                                )}

                                <div className="vlx-signup" data-testid="login-signup-row">
                                    New to VAULT?{" "}
                                    <Link
                                        to="/signup"
                                        data-testid="login-signup-link"
                                        onClick={() => track("login_create_click")}
                                    >
                                        Create your VAULT
                                    </Link>
                                </div>

                                <div className="vlx-reassure">
                                    <Lock size={12} /> VAULT never asks for your CVV, PIN or OTP.
                                </div>
                            </div>
                        </form>
                    )}
                </section>
            </div>
        </div>
    );
}
